import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.jsx'
import { AppProvider, useApp } from '../context/AppContext.jsx'

const ACCESS_EXEMPT_PREFIXES = ['/sem-acesso', '/onboarding']

function isExemptPath(pathname) {
  return ACCESS_EXEMPT_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

function AccessGate() {
  const location = useLocation()
  const ctx = useApp()

  // AppProvider já renderiza seu próprio loader enquanto AccessContext resolve.
  // Se chegamos aqui, ctx está pronto.
  if (!ctx) return null

  const { accessMode, activeOrganizationId } = ctx

  if (accessMode === 'none' && !isExemptPath(location.pathname)) {
    return <Navigate to="/sem-acesso" replace />
  }

  // Issue #94: trocar de organização ativa muda o escopo de TUDO que o
  // backend devolve (header injetado em api.js), mas cada página tem seus
  // próprios hooks de fetch com suas próprias dependências — nem todos
  // reagem a `activeOrganizationId` sozinhos (ex.: useDashboard só refaz a
  // chamada quando `clientId` muda de valor; se o scope agregado permanece
  // `null` nas duas organizações, o hook nunca percebe a troca). Chavear o
  // Outlet por `activeOrganizationId` força o React a desmontar e remontar
  // a página atual inteira quando a organização muda — mesmo efeito de um
  // F5, sem o round-trip de rede real da SPA.
  //
  // Issue #102: em paths isentos (onboarding/sem-acesso) essa troca de key
  // é destrutiva, não útil — o onboarding não está "trocando de org", está
  // GANHANDO a primeira (activeOrganizationId sai de null pra um UUID assim
  // que a org recém-criada é resolvida em loadAccess). Isso remontava
  // <Onboarding/> no meio do wizard, apagando o step e todo o estado local
  // logo após a org (e o client automático) já terem sido persistidos no
  // banco — a tela "travava" de volta no passo 1 sem erro nenhum. Mantém a
  // key estável nesses paths; a remontagem por org só faz sentido dentro do
  // app autenticado normal.
  const outletKey = isExemptPath(location.pathname) ? 'exempt' : activeOrganizationId || 'no-org'
  return <Outlet key={outletKey} />
}

export default function ProtectedRoute() {
  const { user, loading } = useAuth()

  if (loading) {
    return <div className="page-loader">Carregando...</div>
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <AppProvider>
      <AccessGate />
    </AppProvider>
  )
}
